import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getCurrentUserDetails } from '../api/users/user';
import { AuthProviderProps, UserDetails } from '../utils/interface/userListing';
import {
    getStoredUserId,
    setStoredUserId,
    clearStoredUserId,
    clearAllAuthStorage,
    AUTH_SIGNOUT_EVENT,
} from '../utils/authStorage';

interface AuthContextType {
    /** undefined = still loading/unknown, null = signed out/unauthorized */
    userDetails: UserDetails | null | undefined;
    setUserDetails: (details: UserDetails | null) => void;
    refreshUserDetails: () => Promise<void>;
    resetAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [userDetails, setUserDetailsState] = useState<UserDetails | null | undefined>(undefined);
    const isFetchingRef = useRef(false);
    const hasLoadedRef = useRef(false);

    const updateUserDetails = useCallback((details: UserDetails | null) => {
        if (!details) {
            setUserDetailsState(null);
            clearStoredUserId();
            return;
        }
        setUserDetailsState(details);
        setStoredUserId(details.id);
    }, []);

    const refreshUserDetails = useCallback(async () => {
        if (isFetchingRef.current) return;

        try {
            isFetchingRef.current = true;
            const freshDetails = await getCurrentUserDetails();
            if (freshDetails != null) {
                updateUserDetails(freshDetails);
            }
        } catch (error) {
            setUserDetailsState(null);
            clearStoredUserId();
        } finally {
            isFetchingRef.current = false;
        }
    }, [updateUserDetails]);

    // On mount: only call /me when we have a stored userId (i.e. user was previously loaded this session).
    // This avoids calling /me on the login page with no credentials, which would 401 → redirect → reload loop.
    // When user logs in, ProtectedRoute will call refreshUserDetails() and set stored userId.
    useEffect(() => {
        if (hasLoadedRef.current) return;

        const storedUserId = getStoredUserId();
        if (!storedUserId) {
            hasLoadedRef.current = true;
            return;
        }

        hasLoadedRef.current = true;
        const loadUserDetails = async () => {
            try {
                await refreshUserDetails().catch(() => {});
            } catch {
                // ignore
            }
        };

        loadUserDetails();
    }, [refreshUserDetails]);

    // When auth is cleared elsewhere (401, 404, logout from API), clear in-memory state
    useEffect(() => {
        const handleSignOut = () => setUserDetailsState(null);
        window.addEventListener(AUTH_SIGNOUT_EVENT, handleSignOut);
        return () => window.removeEventListener(AUTH_SIGNOUT_EVENT, handleSignOut);
    }, []);

    const setUserDetails = useCallback(
        (details: UserDetails | null) => {
            updateUserDetails(details);
            if (!details) clearStoredUserId();
        },
        [updateUserDetails]
    );

    const resetAuth = useCallback(() => {
        setUserDetailsState(null);
        clearAllAuthStorage();
    }, []);

    const value: AuthContextType = useMemo(
        () => ({
            userDetails,
            setUserDetails,
            refreshUserDetails,
            resetAuth,
        }),
        [userDetails, setUserDetails, refreshUserDetails, resetAuth]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
